import { IColumnTypes, IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import { resolveColumnType } from "./table-column-types";

/**
 * What the Columns tab says is wrong, and where: a row as a whole (outlined) or one of its cells
 * (painted). The local checks here are pure and derived from the rows every time they are read,
 * so an error goes away the moment its cause does; only what the server said has to be stored.
 *
 * Rows are identified by their own object. A column's id can be null and its name can repeat —
 * which is exactly the duplicate case — but every edit replaces the row's object, so an object is
 * one row at one moment, and a stale error cannot follow a row it no longer describes.
 */

/** The cells of a row that can be refused. */
export type ColumnField = "name" | "type" | "size" | "scale" | "default";

/** What is wrong with one row. */
export interface IColumnRowErrors {
  /** The row as a whole — the grid outlines it. */
  row?: string;
  /** A cell's value — the grid paints that cell. */
  fields?: Partial<Record<ColumnField, string>>;
}

/** Errors by row object. */
export type ColumnErrorMap = Map<IDatabaseColumnModel, IColumnRowErrors>;

/** The saved column a row descends from; undefined for a row added since the table was opened. */
export type SavedColumnLookup = (column: IDatabaseColumnModel) => IDatabaseColumnModel | undefined;

export const DUPLICATE_COLUMN_NAME_MESSAGE: string = "Another column already has this name.";

/** Types that carry a length (the Size cell), and the one that carries a scale. */
const SIZED_TYPES: string[] = ["String", "Binary"];
const SCALED_TYPE: string = "Decimal";

/** An integer column's range, as text so Int64's survives — it is past what a double holds exactly. */
interface IIntegerRange {
  min: string;
  max: string;
}

const INTEGER_RANGES: Record<string, IIntegerRange> = {
  Int16: { min: "-32768", max: "32767" },
  Int32: { min: "-2147483648", max: "2147483647" },
  Int64: { min: "-9223372036854775808", max: "9223372036854775807" }
};

const WHOLE_NUMBER: RegExp = /^[+-]?\d+$/;
const DECIMAL_NUMBER: RegExp = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;
const GUID: RegExp = /^\{?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\}?$/;
// Dates are read by shape, not by `Date.parse`, whose reading of anything but ISO depends on the engine.
const ISO_DATE: RegExp = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/** The three rules a column name has always had, which together allow English letters and digits only. */
export const validateColumnName = (name: string): string | null => {
  if (!name) return "Enter a column name.";
  if (/\s/.test(name)) return "Column names cannot contain spaces.";
  if (!/^[a-zA-Z0-9]+$/.test(name)) return "Use English letters and digits only.";
  return null;
};

/** `maxLength` and `decimalScale` arrive as numbers from the grid and as strings from the schema. */
const toNumber = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const parsed: number = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** The same value, whichever of the two shapes it came in. */
const sameValue = (a: unknown, b: unknown): boolean => String(a ?? "") === String(b ?? "");

const validateSize = (column: IDatabaseColumnModel): string | null => {
  if (!SIZED_TYPES.includes(column.kuikaDataType)) return null;
  const size: number | null = toNumber(column.maxLength);
  // -1 is MAX, the absence of a limit.
  if (size === -1) return null;
  if (size === null) return "Enter a size, or choose MAX.";
  if (!Number.isInteger(size) || size < 1) return "Size must be a whole number of at least 1.";
  return null;
};

const validateScale = (column: IDatabaseColumnModel, type: IColumnTypes | undefined): string | null => {
  if (column.kuikaDataType !== SCALED_TYPE) return null;
  const scale: number | null = toNumber(column.decimalScale);
  // An empty scale is the server's to settle; it has always sent one.
  if (scale === null) return null;
  if (!Number.isInteger(scale) || scale < 0) return "Scale must be a whole number of at least 0.";
  const precision: number | null = toNumber(type?.precision);
  if (precision !== null && precision > 0 && scale > precision) {
    return `Scale cannot be more than the precision (${precision}).`;
  }
  return null;
};

const isWithin = (value: string, range: IIntegerRange): boolean => {
  const parsed: bigint = BigInt(value);
  return parsed >= BigInt(range.min) && parsed <= BigInt(range.max);
};

/**
 * Whether a default is one the column's type can hold. The platform's own defaults come first —
 * `New Guid`, `GetDate()`, `Now` are what `defaultValueForType` writes, not values to be parsed.
 */
const validateDefault = (column: IDatabaseColumnModel): string | null => {
  // A default exists only on a NOT NULL column; an empty one is left for the server to settle.
  if (column.nullable !== false || column.defaultValue == null) return null;
  const value: string = String(column.defaultValue).trim();
  if (!value) return null;
  const type: string = column.kuikaDataType;

  const range: IIntegerRange | undefined = INTEGER_RANGES[type];
  if (range) {
    if (WHOLE_NUMBER.test(value) && isWithin(value, range)) return null;
    return `Default must be a whole number between ${range.min} and ${range.max}.`;
  }
  if (type === "Decimal" || type === "Double") {
    return DECIMAL_NUMBER.test(value) ? null : "Default must be a number.";
  }
  if (type === "Guid") {
    return value === "New Guid" || GUID.test(value) ? null : "Default must be a GUID, or New Guid.";
  }
  if (type === "Boolean") {
    const lowered: string = value.toLowerCase();
    return ["true", "false", "1", "0"].includes(lowered) ? null : "Default must be true or false.";
  }
  if (type === "Datetime") {
    return value === "GetDate()" || ISO_DATE.test(value)
      ? null
      : "Default must be GetDate() or a date written YYYY-MM-DD.";
  }
  if (type === "DatetimeOffset") {
    return value === "Now" || ISO_DATE.test(value) ? null : "Default must be Now or a date written YYYY-MM-DD.";
  }
  if (type === "String") {
    const size: number | null = toNumber(column.maxLength);
    if (size !== null && size >= 1 && value.length > size) return `Default is longer than the column's size (${size}).`;
  }
  return null;
};

/** Which of a row's values were changed since it was saved; all of them for a row that is new. */
interface IChangedValues {
  name: boolean;
  type: boolean;
  size: boolean;
  scale: boolean;
  default: boolean;
}

const changedValues = (column: IDatabaseColumnModel, saved: IDatabaseColumnModel | undefined): IChangedValues => {
  if (!saved) return { name: true, type: true, size: true, scale: true, default: true };
  const type: boolean = column.kuikaDataType !== saved.kuikaDataType;
  return {
    name: column.name !== saved.name,
    type,
    // A new type resets the length, the scale and the default, so they are the new type's to check.
    size: type || !sameValue(column.maxLength, saved.maxLength),
    scale: type || !sameValue(column.decimalScale, saved.decimalScale),
    default: type || column.nullable !== saved.nullable || !sameValue(column.defaultValue, saved.defaultValue)
  };
};

/**
 * What UPDATE would send that the database would refuse or the model cannot hold. Only values
 * changed since the table was opened are checked — a saved column holds the database's own
 * values (an underscore in a name, `((0))` as a default), and a save must never be refused over
 * something nobody touched. A duplicate name is a clash between rows, so every row of the clash is
 * marked — but only when a new or renamed row is part of it: a pair the database already holds
 * (`Name` and `name` under quoted identifiers) is the database's, and not this save's to refuse.
 */
export const validateColumns = (
  columns: IDatabaseColumnModel[],
  savedOf: SavedColumnLookup,
  columnTypes: IColumnTypes[]
): ColumnErrorMap => {
  const errors: ColumnErrorMap = new Map<IDatabaseColumnModel, IColumnRowErrors>();
  const setField = (column: IDatabaseColumnModel, field: ColumnField, message: string | null): void => {
    if (!message) return;
    const entry: IColumnRowErrors = errors.get(column) ?? {};
    // The first finding on a cell is the one it shows.
    if (entry.fields?.[field]) return;
    errors.set(column, { ...entry, fields: { ...entry.fields, [field]: message } });
  };

  const changes: Map<IDatabaseColumnModel, IChangedValues> = new Map<IDatabaseColumnModel, IChangedValues>();
  // Per lower-cased name: how many rows carry it, and whether one of them was given it here.
  const nameCounts: Map<string, number> = new Map<string, number>();
  const namesGivenHere: Set<string> = new Set<string>();
  columns.forEach((column: IDatabaseColumnModel): void => {
    const changed: IChangedValues = changedValues(column, savedOf(column));
    changes.set(column, changed);
    const key: string = (column.name ?? "").toLowerCase();
    nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    if (changed.name) namesGivenHere.add(key);
  });

  columns.forEach((column: IDatabaseColumnModel): void => {
    const changed: IChangedValues = changes.get(column) ?? changedValues(column, savedOf(column));
    if (changed.name) setField(column, "name", validateColumnName(column.name ?? ""));
    const key: string = (column.name ?? "").toLowerCase();
    if ((nameCounts.get(key) ?? 0) > 1 && namesGivenHere.has(key)) {
      setField(column, "name", DUPLICATE_COLUMN_NAME_MESSAGE);
    }
    if (changed.type && !column.kuikaDataType) setField(column, "type", "Choose a data type.");
    if (changed.size) setField(column, "size", validateSize(column));
    if (changed.scale) {
      setField(column, "scale", validateScale(column, resolveColumnType(columnTypes, column.kuikaDataType)));
    }
    if (changed.default) setField(column, "default", validateDefault(column));
  });
  return errors;
};

/** The cell each model field is edited in: a Required change resets the default, so it counts as one. */
const FIELD_OF_PROPERTY: Partial<Record<keyof IDatabaseColumnModel, ColumnField>> = {
  name: "name",
  kuikaDataType: "type",
  sqlDataType: "type",
  maxLength: "size",
  decimalScale: "scale",
  defaultValue: "default",
  nullable: "default"
};

/** Which cells an edit of these properties answers. */
export const editedColumnFields = (patch: Partial<IDatabaseColumnModel>): ColumnField[] => {
  const fields: ColumnField[] = [];
  (Object.keys(patch) as (keyof IDatabaseColumnModel)[]).forEach((property: keyof IDatabaseColumnModel): void => {
    const field: ColumnField | undefined = FIELD_OF_PROPERTY[property];
    if (field && !fields.includes(field)) fields.push(field);
  });
  return fields;
};

/**
 * What the server said about a row, carried onto the object an edit of `fields` replaced it with.
 * Every edit to the row drops a migration failure — the row is no longer what failed — while a
 * refused name stays until the name itself is edited.
 */
export const carryServerErrors = (
  entry: IColumnRowErrors | undefined,
  editedFields: ColumnField[]
): IColumnRowErrors | undefined => {
  const fields: Partial<Record<ColumnField, string>> = { ...entry?.fields };
  editedFields.forEach((field: ColumnField): void => {
    delete fields[field];
  });
  return Object.keys(fields).length > 0 ? { fields } : undefined;
};

/** The two sources, row by row; where both speak for one cell the local finding shows. */
export const mergeColumnErrors = (local: ColumnErrorMap, server: ColumnErrorMap): ColumnErrorMap => {
  const merged: ColumnErrorMap = new Map<IDatabaseColumnModel, IColumnRowErrors>(server);
  local.forEach((entry: IColumnRowErrors, column: IDatabaseColumnModel): void => {
    const current: IColumnRowErrors | undefined = merged.get(column);
    merged.set(column, { ...current, ...entry, fields: { ...current?.fields, ...entry.fields } });
  });
  return merged;
};

/** The one toast an UPDATE with problems raises; the rows themselves say what is wrong. */
export const columnsNeedAttentionMessage = (count: number): string =>
  count === 1 ? "1 column needs attention" : `${count} columns need attention`;
